import React, { useEffect, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { QrCode, Plus, Download, Trash2, ScanLine, ExternalLink } from 'lucide-react';
import { useProject } from '@/lib/ProjectContext';
import { qrCodesAPI } from '@/lib/api';
import { useT } from '@/lib/LanguageContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ALL = '__all__';
const NONE = '__none__';

export default function QRCodes() {
  const t = useT();
  const { activeProject, projects } = useProject();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ label: '', destination_url: '', project_id: '' });
  const [creating, setCreating] = useState(false);
  const [projectFilter, setProjectFilter] = useState(activeProject?._id || ALL);

  useEffect(() => {
    fetchCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter]);

  async function fetchCodes() {
    setLoading(true);
    try {
      const params = (projectFilter !== ALL && projectFilter !== NONE) ? { project_id: projectFilter } : {};
      const res = await qrCodesAPI.list(params);
      const list = Array.isArray(res.data) ? res.data : [];
      setCodes(projectFilter === NONE ? list.filter((c) => !c.project_id) : list);
    } catch {
      toast.error('Failed to load QR codes');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setForm({
      label: '',
      destination_url: '',
      project_id: (projectFilter !== ALL && projectFilter !== NONE) ? projectFilter : '',
    });
    setShowCreate(true);
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.label.trim() || !form.destination_url.trim()) return;
    setCreating(true);
    try {
      const res = await qrCodesAPI.create({
        project_id: form.project_id || null,
        label: form.label.trim(),
        destination_url: form.destination_url.trim(),
      });
      const created = { ...res.data, scan_count: 0 };
      // Only splice it into the current view if it actually belongs there
      const belongs =
        projectFilter === ALL ||
        (projectFilter === NONE ? !created.project_id : created.project_id === projectFilter);
      if (belongs) setCodes((prev) => [created, ...prev]);
      setShowCreate(false);
      toast.success('QR code created');
    } catch {
      toast.error('Failed to create QR code');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id) {
    try {
      await qrCodesAPI.delete(id);
      setCodes((prev) => prev.filter((c) => c._id !== id));
      setDeleteTarget(null);
      toast.success('QR code deleted');
    } catch {
      toast.error('Failed to delete QR code');
    }
  }

  function downloadQR(id, label, format) {
    qrCodesAPI.download(id, format, label);
  }

  return (
    <Layout title={t('qrcodes.title')}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('qrcodes.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Generate trackable QR codes, project-specific or company-wide.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="h-9 w-[180px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All Projects</SelectItem>
                <SelectItem value={NONE}>No project</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={openCreate} className="gap-2">
              <Plus className="w-4 h-4" />
              {t('qrcodes.new')}
            </Button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Total Codes</p>
              <p className="text-3xl font-bold mt-1">{codes.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Total Scans</p>
              <p className="text-3xl font-bold mt-1">
                {codes.reduce((acc, c) => acc + (c.scan_count || 0), 0)}
              </p>
            </CardContent>
          </Card>
          <Card className="hidden sm:block">
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Top QR Code</p>
              <p className="text-lg font-semibold mt-1 truncate">
                {codes.length
                  ? [...codes].sort((a, b) => (b.scan_count || 0) - (a.scan_count || 0))[0].label
                  : '—'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* QR Code list */}
        {loading ? (
          <div className="text-center text-muted-foreground py-16">{t('qrcodes.loading')}</div>
        ) : codes.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-20 gap-4">
              <QrCode className="w-12 h-12 text-muted-foreground/40" />
              <p className="text-muted-foreground text-sm">{t('qrcodes.none')}</p>
              <Button onClick={openCreate} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" />
                {t('qrcodes.new')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {codes.map((code) => (
              <Card key={code._id} className="flex flex-col">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base font-semibold truncate">{code.label}</CardTitle>
                    <Badge variant="secondary" className="shrink-0 gap-1">
                      <ScanLine className="w-3 h-3" />
                      {code.scan_count ?? 0} {t('qrcodes.scans')}
                    </Badge>
                  </div>
                  <a
                    href={code.destination_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-primary truncate flex items-center gap-1 mt-1"
                  >
                    <ExternalLink className="w-3 h-3 shrink-0" />
                    {code.destination_url}
                  </a>
                </CardHeader>
                <CardContent className="pt-0 flex-1 flex flex-col justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    Created {code.created_at ? format(new Date(code.created_at), 'MMM d, yyyy') : '—'}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 gap-1 text-xs"
                      onClick={() => downloadQR(code._id, code.label, 'png')}
                    >
                      <Download className="w-3 h-3" />
                      PNG
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 gap-1 text-xs"
                      onClick={() => downloadQR(code._id, code.label, 'svg')}
                    >
                      <Download className="w-3 h-3" />
                      SVG
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setDeleteTarget(code)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('qrcodes.new')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="label">Label</Label>
              <Input
                id="label"
                placeholder="e.g. Tradeshow Booth, Brochure Oct 2026"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="destination_url">Destination URL</Label>
              <Input
                id="destination_url"
                type="url"
                placeholder="https://yoursite.com/landing"
                value={form.destination_url}
                onChange={(e) => setForm((f) => ({ ...f, destination_url: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Select
                value={form.project_id || NONE}
                onValueChange={(v) => setForm((f) => ({ ...f, project_id: v === NONE ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No project (company-wide)</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? t('common.loading') : t('qrcodes.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete QR Code?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deleteTarget?.label}</strong> and all its scan
              history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => handleDelete(deleteTarget._id)}
            >
              {t('qrcodes.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
